import { isEmpty } from 'lodash'
import {
  AccountType,
  AlpsAcatDetailV2WebDto,
  AuditTrail,
} from '../../../../api/types'
import { Asset } from '../../../../types/Asset'
import { formatDateAndTime } from '../../../../utils/dateUtil'
import {
  getAlpsState,
  getCorrespondentName,
  getDirectionMessage,
  getNsccState,
  getRecommendedAction,
  getRejectReason,
  getTransferTypeMessage,
} from './messages'
import { formatAssetAmount } from './formatAssetAmount'

export const assembleTransferDetailsCsv = (
  transferDetails: AlpsAcatDetailV2WebDto,
  auditTrail: AuditTrail[],
  assets: Asset[]
) => {
  const assembleTransferDetailRow = (
    definitionTerm: string,
    definitionValue: string | null
  ) => `${definitionTerm},${quoteValue(definitionValue || '—')}`
  const assembleAccountInformationRow = (
    definitionTerm: string,
    recevingAccountDefinitionValue: string | null,
    deliveringAccountDefinitionValue: string | null
  ) =>
    `${definitionTerm},${quoteValue(
      recevingAccountDefinitionValue || '—'
    )},${definitionTerm},${quoteValue(deliveringAccountDefinitionValue || '—')}`
  const getAccountType = (accountTypes: AccountType[]) => {
    return isEmpty(accountTypes) ? '' : accountTypes.join(', ')
  }
  const quoteValue = (value: string | null) => `"${value}"`

  const transferDetailRows = [
    assembleTransferDetailRow(
      'Control Number',
      `${transferDetails.acatsControlNumber}`
    ),
    assembleTransferDetailRow(
      'Created',
      transferDetails.created && formatDateAndTime(transferDetails.created)
    ),
    assembleTransferDetailRow(
      'Transfer Type',
      getTransferTypeMessage(transferDetails.transferType)
    ),
    assembleTransferDetailRow(
      'Direction',
      getDirectionMessage(transferDetails.direction)
    ),
    assembleTransferDetailRow(
      'Correspondent',
      getCorrespondentName(transferDetails)
    ),
    assembleTransferDetailRow(
      'Memo',
      `${transferDetails.status.alpsActionReason} ${transferDetails.status.notes}`
    ),
    assembleTransferDetailRow('Public Key', transferDetails.anonymousKey),
    assembleTransferDetailRow(
      'NSCC State',
      getNsccState(transferDetails.status?.nsccState)
    ),
    assembleTransferDetailRow(
      'Next Action',
      getRecommendedAction(transferDetails.status?.action)
    ),
    assembleTransferDetailRow(
      'ALPS State',
      getAlpsState(transferDetails.status?.state)
    ),
    assembleTransferDetailRow(
      'Reject Reason',
      getRejectReason(transferDetails.status?.rejectReason)
    ),
    assembleTransferDetailRow(
      'Comment',
      transferDetails.caseStates?.[0]?.comment
    ),
    assembleTransferDetailRow(
      'Recommended Action',
      getRecommendedAction(transferDetails.status.alpsRecommendedAction)
    ),
    assembleTransferDetailRow(
      'Override Action',
      getRecommendedAction(transferDetails.caseStates?.[0]?.override)
    ),
    assembleTransferDetailRow(
      'Scheduled',
      transferDetails.status.isScheduled ? 'Yes' : 'No'
    ),
    assembleTransferDetailRow(
      'Submitted',
      transferDetails.status.isSubmitted ? 'Yes' : 'No'
    ),
  ]

  const accountInformationRows = [
    'Receiving Account,,Delivering Account,',
    assembleAccountInformationRow(
      'Title',
      transferDetails.receiver.accountTitle,
      transferDetails.deliverer.accountTitle
    ),
    assembleAccountInformationRow(
      'Number',
      transferDetails.receiver.accountNumber,
      transferDetails.deliverer.accountNumber
    ),
    assembleAccountInformationRow(
      'Type',
      getAccountType(transferDetails.receiver.accountTypes),
      getAccountType(transferDetails.deliverer.accountTypes)
    ),
    assembleAccountInformationRow(
      'Cash/Margin',
      transferDetails.receiver.isMarginAccount ? 'Margin' : 'Cash',
      transferDetails.deliverer.isMarginAccount ? 'Margin' : 'Cash'
    ),
    assembleAccountInformationRow(
      'Firm',
      transferDetails.receiver.participantName,
      transferDetails.deliverer.participantName
    ),
    assembleAccountInformationRow(
      'SSN/Tax ID',
      transferDetails.receiver.primarySsnOrTaxId,
      transferDetails.deliverer.primarySsnOrTaxId
    ),
  ]

  const assembleAssetRow = (asset: Asset) => {
    const displayAmount = formatAssetAmount(asset)
    return `${quoteValue(asset.cusip)},${quoteValue(asset.symbol)},${quoteValue(
      asset.quantity.toString()
    )},${quoteValue(asset.accountType)},${quoteValue(
      asset.description
    )},${quoteValue(asset.settlingLocation)},${quoteValue(
      asset.settlingLocationReason
    )},${quoteValue(displayAmount)},${quoteValue(asset.currency)}`
  }

  const assetRows = [
    'Assets',
    'CUSIP,Symbol,Quantity,Cash Margin Short Code,Description,Settling Location,Settling Location Reason,Delivering?,Amount,Currency',
  ].concat(assets.map(assembleAssetRow))

  const auditTrailRows = ['Audit Trail', 'Date & Time,User,Type,Note'].concat(
    auditTrail.map(
      audit =>
        `${quoteValue(audit.timestamp)},${quoteValue(
          audit.username
        )},${quoteValue(audit.type)},${quoteValue(audit.comment)}`
    )
  )

  const csvContent = `data:text/csv;charset=utf-8,Transfer Details\r\n${transferDetailRows.join(
    '\r\n'
  )}\r\n\r\n${accountInformationRows.join('\r\n')}\r\n\r\n${assetRows.join(
    '\r\n'
  )}\r\n\r\n${auditTrailRows.join('\r\n')}`
  return encodeURI(csvContent)
}
